import React from 'react';
import { Box, Button, Typography, Card, CardContent, Chip } from '@mui/material';
import { Lock as LockIcon, ArrowForward as ArrowForwardIcon, Email as EmailIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { FILM_TIER_NAMES, FILM_TIERS } from '../../lib/filmSubscriptionTiers';
import { brandColors } from '../../theme/theme';

/**
 * UpgradePrompt Component
 * 
 * Displays upgrade CTA for tier-gated features
 */
const UpgradePrompt = ({ tier, featureName }) => {
  const navigate = useNavigate();
  const tierName = FILM_TIER_NAMES[tier] || tier;
  const isEnterprise = tier === FILM_TIERS.ENTERPRISE;

  const handleUpgradeClick = () => {
    if (isEnterprise) {
      // For Enterprise, scroll to contact form or open email
      window.location.href = 'mailto:sales@greenlightanalytics.com?subject=Film%20Enterprise%20Inquiry';
    } else {
      // Navigate to subscription page
      navigate('/subscription/film');
    }
  };

  const getUpgradeMessage = () => {
    switch (tier) {
      case FILM_TIERS.BASIC:
        return 'Unlock Film Dashboard';
      case FILM_TIERS.PROFESSIONAL:
        return 'Unlock Film Analytics';
      case FILM_TIERS.ENTERPRISE:
        return 'Unlock Deep Dive Analytics';
      default:
        return 'Unlock This Feature';
    }
  };

  const getUpgradeDescription = () => {
    switch (tier) {
      case FILM_TIERS.BASIC:
        return 'Get full access to Film Dashboard with real-time survey data and insights';
      case FILM_TIERS.PROFESSIONAL:
        return 'Access detailed film analytics, comps database, and box office projections';
      case FILM_TIERS.ENTERPRISE:
        return 'Get advanced demographic analytics, behavioral insights, and multi-user company accounts';
      default:
        return 'Upgrade your subscription to access this feature';
    }
  };

  return (
    <Card 
      elevation={8}
      sx={{ 
        maxWidth: 500,
        width: '90%',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.98) 100%)',
        backdropFilter: 'blur(10px)',
        border: `2px solid ${brandColors.primary}20`
      }}
    >
      <CardContent sx={{ p: 4, textAlign: 'center' }}>
        {/* Lock Icon */}
        <Box 
          sx={{ 
            display: 'inline-flex',
            p: 2,
            borderRadius: '50%',
            bgcolor: `${brandColors.primary}15`,
            mb: 2
          }}
        >
          <LockIcon sx={{ fontSize: 40, color: brandColors.primary }} />
        </Box>

        {/* Tier Badge */}
        <Chip 
          label={tierName}
          sx={{ 
            mb: 2,
            fontWeight: 600,
            bgcolor: brandColors.primary,
            color: 'white',
            fontSize: '0.875rem',
            height: 32
          }}
        />

        {/* Title */}
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 700,
            mb: 1,
            color: brandColors.secondary
          }}
        >
          {getUpgradeMessage()}
        </Typography>

        {/* Description */}
        <Typography 
          variant="body1" 
          sx={{ 
            mb: 3,
            color: 'text.secondary',
            lineHeight: 1.6
          }}
        >
          {getUpgradeDescription()}
        </Typography>

        {/* CTA Button */}
        {isEnterprise ? (
          <Button
            variant="contained"
            size="large"
            startIcon={<EmailIcon />}
            onClick={handleUpgradeClick}
            sx={{
              bgcolor: brandColors.primary,
              color: 'white',
              py: 1.5,
              px: 4,
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 3,
              '&:hover': {
                bgcolor: brandColors.primaryDark,
                boxShadow: 6
              }
            }}
          >
            Contact Sales
          </Button>
        ) : (
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={handleUpgradeClick}
            sx={{
              bgcolor: brandColors.primary,
              color: 'white',
              py: 1.5,
              px: 4,
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 3,
              '&:hover': {
                bgcolor: brandColors.primaryDark,
                boxShadow: 6
              }
            }}
          >
            Upgrade to {tierName}
          </Button>
        )}

        {/* Additional Info */}
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block',
            mt: 2,
            color: 'text.secondary'
          }}
        >
          {isEnterprise 
            ? 'Get custom pricing and dedicated support'
            : 'Start your free trial today • No credit card required'
          }
        </Typography>
      </CardContent>
    </Card>
  );
};

export default UpgradePrompt;

